import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Globe, ArrowRight, Users, BookOpen, Award, Target } from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgramsPage = () => {
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [programsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/cms/programs`),
          axios.get(`${API}/cms/settings`)
        ]);
        setContent(programsRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Failed to load programs content');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const { programs } = content || {};
  const programTypes = ['all', ...new Set(programs?.map(p => p.program_type).filter(Boolean))];
  const filteredPrograms = activeFilter === 'all' 
    ? programs 
    : programs?.filter(p => p.program_type === activeFilter);

  const typeIcons = {
    training: BookOpen,
    governance: Award,
    talent: Users,
    default: Target
  };

  return (
    <div className="min-h-screen bg-background">
      <MegaMenu />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-primary/10 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-primary text-sm font-medium mb-6">
              Our Programs
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Transformative Sports<br />Programs
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto">
              Discover our range of programs designed to develop talent, build capacity, 
              and drive growth across the African sports ecosystem.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-card-bg border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-white/60 text-sm">Filter by type:</span>
            {programTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveFilter(type)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                  activeFilter === type
                    ? 'bg-primary text-white'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`filter-${type}`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {filteredPrograms?.map((program, idx) => {
              const IconComponent = typeIcons[program.program_type] || typeIcons.default;
              return (
                <motion.div
                  key={program.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-card-bg border border-border rounded-2xl overflow-hidden hover:border-primary/50 transition-all"
                  data-testid={`program-card-${program.slug}`}
                >
                  {/* Image */}
                  <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center relative">
                    {program.image ? (
                      <img src={program.image} alt={program.name} className="w-full h-full object-cover" />
                    ) : (
                      <IconComponent className="w-20 h-20 text-white/10" />
                    )}
                    <span className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur-sm text-primary text-xs font-medium rounded-full uppercase">
                      {program.program_type}
                    </span>
                    {program.is_featured && (
                      <span className="absolute top-4 right-4 px-3 py-1 bg-primary text-white text-xs font-medium rounded-full">
                        Featured
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h2 className="text-2xl font-heading font-bold text-white mb-2 group-hover:text-primary transition-colors">
                      {program.name}
                    </h2>
                    {program.tagline && (
                      <p className="text-primary text-sm font-medium mb-3">{program.tagline}</p>
                    )}
                    <p className="text-white/60 text-sm leading-relaxed mb-4">
                      {program.description}
                    </p>

                    {/* Meta */}
                    <div className="flex flex-wrap gap-4 text-xs text-white/50 mb-4">
                      {program.duration && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                          {program.duration}
                        </span>
                      )}
                      {program.target_audience && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-secondary rounded-full" />
                          {program.target_audience}
                        </span>
                      )}
                      {program.price && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                          KES {program.price.toLocaleString()}
                        </span>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="flex items-center gap-4">
                      <Link
                        to={program.enrollment_link || '/contact'}
                        className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                      >
                        Enroll Now <ArrowRight size={16} />
                      </Link>
                      <Link
                        to={`/programs/${program.slug}`}
                        className="text-white/60 hover:text-primary text-sm transition-colors"
                      >
                        Learn more
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {(!filteredPrograms || filteredPrograms.length === 0) && (
            <div className="text-center py-20">
              <Globe className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-bold text-white mb-2">No Programs Found</h3>
              <p className="text-white/60">Check back soon for new programs.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/20 via-background to-secondary/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Custom Program Development
          </h2>
          <p className="text-xl text-white/70 mb-10">
            Need a tailored program for your organization? We design custom training 
            and development solutions for sports bodies across Africa.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl transition-all"
            data-testid="contact-cta"
          >
            Contact Us <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
};

export default ProgramsPage;
